import { makeObservable, computed, observable, runInAction } from 'mobx';
import { uniq } from 'lodash';
import { Disposer } from '../../../custom_shared/misc';
import { InputIds } from '../../misc';
import { ContractChangeTracker } from '../../change_trackers';
import { ContractComparisonPanel, ContractComparisonSummaryEntry } from '../../types';
import { InputBaseModel } from '../../models';

export default abstract class ComparisonNodeBaseModel extends Disposer {
    contractChangeTracker: ContractChangeTracker = new ContractChangeTracker();

    summaryEntries: ContractComparisonSummaryEntry[] = [];

    readonly hiddenInputIds: string[] = [
        // Create program is not part of SICS response
        InputIds.GeneralInformation.CreateProgram,
        // Business type is the same and we can't allow to change it in comparison view it will cause a lot of layout changes
        InputIds.GeneralInformation.BusinessType,
        // Brokerage type is not part of SICS response
        InputIds.Reinstatements.BrokerageType
    ];

    constructor(
        readonly nodeId: string,
        readonly nodeName: string,
        readonly predefinedPanels?: ContractComparisonPanel[]
    ) {
        super();

        makeObservable(this, {
            summaryEntries: observable,
            panels: computed
        });
    }

    generateSummaryEntries() {
        runInAction(() => {
            this.summaryEntries = Array.from(this.contractChangeTracker.inputGroupChangeTrackers.values()).map(
                inputGroupChangeTracker => ({
                    entryName: inputGroupChangeTracker.groupName,
                    changes: inputGroupChangeTracker.aggregatedChanges.changeRecords.map(record => ({
                        inputId: record.inputId,
                        inputName: record.inputName,
                        newValue: this.getChangeRecordValue(record.newInput?.dataType, record.newInput),
                        previousValue: this.getChangeRecordValue(record.previousInput?.dataType, record.previousInput),
                        // For summary we consider unsaved changes as modified
                        changeType: record.changeType === 'unsaved' ? 'modified' : record.changeType
                    })),
                    // TODO: Add sections when we will have nested groups (e.g. sections, layers with sections)
                    sections: []
                })
            );
        });
    }

    dispose() {
        this.contractChangeTracker.clearInputGroupChangeTrackers();
        this.disposeReactions();
    }

    abstract get inputIds(): string[];

    protected getInputIds(newNodeInputIds: string[] = [], previousNodeInputIds: string[] = []): string[] {
        return uniq([...newNodeInputIds, ...previousNodeInputIds]).filter(
            inputId =>
                !this.hiddenInputIds.includes(inputId) &&
                // Extra limits and deductions are skipped for now, UI must contain only unique codes, which will be implemented in separate task
                !inputId.startsWith(InputIds.Limits.LimitType) &&
                !inputId.startsWith(InputIds.Limits.LimitValue) &&
                !inputId.startsWith(InputIds.Deductions.DeductionType) &&
                !inputId.startsWith(InputIds.Deductions.DeductionValue)
        );
    }

    get panels() {
        // Use predefined panels if provided
        if (this.predefinedPanels) {
            return this.predefinedPanels.map(panel => ({
                ...panel,
                inputIds: panel.inputIds.filter(inputId => this.inputIds.includes(inputId))
            }));
        }

        // Otherwise, create a default panel with all input ids
        return [
            {
                id: this.nodeId,
                name: this.nodeName,
                inputIds: this.inputIds
            }
        ];
    }

    private getChangeRecordValue(dataType: string | undefined, input: InputBaseModel | undefined): unknown {
        if (dataType === 'DateTime') {
            return input?.dto.value ?? null;
        }

        return input?.formattedValue ?? null;
    }
}
